import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Truck, Package, Plus, Trash, Tag, PencilSimple, X, Check, Warning } from "@phosphor-icons/react";
import { invalidateReceptionStatuses, iconForStatus, withAlpha } from "@/lib/receptionStatuses";

export default function Catalogs() {
  return (
    <div>
      <div className="mb-8">
        <div className="font-mono-data text-[11px] uppercase tracking-[0.3em] text-[#0055FF] mb-2">
          Administración · Catálogos
        </div>
        <h1 className="font-display text-4xl sm:text-5xl">Catálogos Maestros</h1>
        <p className="text-white/50 text-sm mt-2">
          Gestiona proveedores, tipos de unidad y estatus de recepción que se usan en el flujo operativo.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <CatalogPanel
          title="Proveedores"
          icon={Truck}
          endpoint="/catalogs/providers"
          testId="providers"
          placeholderName="Nombre del proveedor"
          placeholderCode="Código (opcional)"
        />
        <CatalogPanel
          title="Tipos de Unidad"
          icon={Package}
          endpoint="/catalogs/unit-types"
          testId="unit-types"
          placeholderName="Nombre (ej. Caja Seca 53')"
          placeholderCode="Código (ej. CS53)"
        />
      </div>

      <div className="mt-6">
        <ReceptionStatusPanel />
      </div>

      <div className="mt-6">
        <RejectionCausesPanel />
      </div>
    </div>
  );
}

function CatalogPanel({ title, icon: Icon, endpoint, testId, placeholderName, placeholderCode }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Ingresa un nombre");
      return;
    }
    setBusy(true);
    try {
      await api.post(endpoint, { name: name.trim(), code: code.trim() });
      toast.success("Agregado");
      setName("");
      setCode("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`¿Eliminar "${item.name}"?`)) return;
    try {
      await api.delete(`${endpoint}/${item.id}`);
      toast.success("Eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="bg-[#111111] border border-white/10 rounded-sm">
      <div className="p-5 border-b border-white/10 flex items-center gap-3">
        <Icon size={20} className="text-[#0055FF]" weight="duotone" />
        <div className="flex-1">
          <div className="text-[10px] font-mono-data uppercase tracking-widest text-white/50">
            Catálogo
          </div>
          <div className="font-display text-lg">{title}</div>
        </div>
        <span
          data-testid={`${testId}-count`}
          className="font-mono-data text-xs px-2 py-0.5 rounded-sm bg-white/5 border border-white/10 text-white/60"
        >
          {items.length}
        </span>
      </div>

      <form onSubmit={add} className="p-4 border-b border-white/10 space-y-2">
        <input
          data-testid={`${testId}-name-input`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={placeholderName}
          className="w-full px-3 py-2 rounded-sm bg-[#0A0A0A] border border-white/10 text-sm text-white placeholder:text-white/30 focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] transition-colors outline-none"
        />
        <div className="flex gap-2">
          <input
            data-testid={`${testId}-code-input`}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={placeholderCode}
            className="flex-1 px-3 py-2 rounded-sm bg-[#0A0A0A] border border-white/10 text-sm text-white placeholder:text-white/30 focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] transition-colors outline-none"
          />
          <button
            data-testid={`${testId}-add-btn`}
            disabled={busy}
            className="flex items-center gap-1.5 px-4 py-2 rounded-sm bg-[#0055FF] text-white text-xs font-semibold hover:brightness-110 active:scale-[0.98] transition-transform disabled:opacity-50"
          >
            <Plus size={14} /> Agregar
          </button>
        </div>
      </form>

      <div className="max-h-[520px] overflow-y-auto">
        {loading ? (
          <div className="p-6 text-white/50 text-sm">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center border border-dashed border-white/10 m-4 rounded-sm">
            <div className="text-white/60 text-sm">Sin registros</div>
            <div className="text-white/40 text-xs mt-1">Agrega el primero arriba</div>
          </div>
        ) : (
          <ul>
            {items.map((it) => (
              <li
                key={it.id}
                data-testid={`${testId}-row`}
                className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 hover:bg-white/5 transition-colors group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Tag size={14} className="text-white/30 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{it.name}</div>
                    {it.code && (
                      <div className="font-mono-data text-[10px] text-white/40 uppercase tracking-wider">
                        {it.code}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => remove(it)}
                  data-testid={`${testId}-delete-btn`}
                  className="p-1.5 rounded-sm text-white/40 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors opacity-0 group-hover:opacity-100"
                >
                  <Trash size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const PRESET_COLORS = [
  "#A78BFA", "#F59E0B", "#06B6D4", "#10B981", "#0055FF",
  "#FACC15", "#FF3B30", "#EC4899", "#8B5CF6", "#22D3EE",
];

function ReceptionStatusPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");
  const [color, setColor] = useState(PRESET_COLORS[0]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // {id, label, color}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/catalogs/reception-statuses");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!label.trim()) {
      toast.error("Ingresa una etiqueta");
      return;
    }
    setBusy(true);
    try {
      await api.post("/catalogs/reception-statuses", {
        label: label.trim(),
        color,
        order: items.length + 1,
      });
      toast.success("Estatus agregado");
      setLabel("");
      setColor(PRESET_COLORS[0]);
      await load();
      invalidateReceptionStatuses();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.put(`/catalogs/reception-statuses/${editing.id}`, {
        label: editing.label,
        color: editing.color,
      });
      toast.success("Estatus actualizado");
      setEditing(null);
      await load();
      invalidateReceptionStatuses();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`¿Eliminar el estatus "${item.label}"?`)) return;
    try {
      await api.delete(`/catalogs/reception-statuses/${item.id}`);
      toast.success("Eliminado");
      await load();
      invalidateReceptionStatuses();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="bg-[#111111] border border-white/10 rounded-sm">
      <div className="p-5 border-b border-white/10 flex items-center gap-3">
        <Package size={20} className="text-[#0055FF]" weight="duotone" />
        <div className="flex-1">
          <div className="text-[10px] font-mono-data uppercase tracking-widest text-white/50">
            Catálogo · Dinámico
          </div>
          <div className="font-display text-lg">Estatus de Recepción</div>
          <div className="text-xs text-white/50 mt-1">
            Estos estatus aparecen en el chip de recepción del listado de citas y en los filtros.
          </div>
        </div>
        <span
          data-testid="reception-statuses-count"
          className="font-mono-data text-xs px-2 py-0.5 rounded-sm bg-white/5 border border-white/10 text-white/60"
        >
          {items.length}
        </span>
      </div>

      <form onSubmit={add} className="p-4 border-b border-white/10 grid md:grid-cols-[1fr_auto_auto] gap-2 items-start">
        <input
          data-testid="reception-label-input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Etiqueta (ej. En muelle)"
          className="px-3 py-2 rounded-sm bg-[#0A0A0A] border border-white/10 text-sm text-white placeholder:text-white/30 focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] outline-none"
        />
        <ColorPicker value={color} onChange={setColor} />
        <button
          data-testid="reception-add-btn"
          disabled={busy}
          className="flex items-center gap-1.5 px-4 py-2 rounded-sm bg-[#0055FF] text-white text-xs font-semibold hover:brightness-110 active:scale-[0.98] transition-transform disabled:opacity-50"
        >
          <Plus size={14} /> Agregar estatus
        </button>
      </form>

      <div className="max-h-[520px] overflow-y-auto">
        {loading ? (
          <div className="p-6 text-white/50 text-sm">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center border border-dashed border-white/10 m-4 rounded-sm">
            <div className="text-white/60 text-sm">Sin estatus</div>
          </div>
        ) : (
          <ul>
            {items.map((it) => {
              const Icon = iconForStatus(it);
              const isEditing = editing?.id === it.id;
              return (
                <li
                  key={it.id}
                  data-testid="reception-row"
                  className="flex items-center gap-3 px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors group"
                >
                  {isEditing ? (
                    <>
                      <input
                        value={editing.label}
                        onChange={(e) => setEditing((x) => ({ ...x, label: e.target.value }))}
                        className="flex-1 px-2 py-1.5 rounded-sm bg-[#0A0A0A] border border-white/10 text-sm text-white outline-none focus:border-[#0055FF]"
                        data-testid="reception-edit-label"
                      />
                      <ColorPicker
                        value={editing.color}
                        onChange={(c) => setEditing((x) => ({ ...x, color: c }))}
                      />
                      <button
                        onClick={saveEdit}
                        data-testid="reception-save-edit"
                        className="p-1.5 rounded-sm text-[#10B981] hover:bg-[#10B981]/10"
                      >
                        <Check size={16} weight="bold" />
                      </button>
                      <button
                        onClick={() => setEditing(null)}
                        className="p-1.5 rounded-sm text-white/50 hover:bg-white/5"
                      >
                        <X size={16} />
                      </button>
                    </>
                  ) : (
                    <>
                      <span
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm font-mono-data text-[10px] uppercase tracking-widest border"
                        style={{
                          color: it.color,
                          backgroundColor: withAlpha(it.color, 0.14),
                          borderColor: `${it.color}55`,
                        }}
                      >
                        <Icon size={11} weight="fill" />
                        {it.label}
                      </span>
                      <span className="font-mono-data text-[10px] text-white/30 uppercase tracking-wider">
                        {it.key}
                      </span>
                      <div className="flex-1" />
                      {it.is_default && (
                        <span className="font-mono-data text-[9px] uppercase tracking-widest text-white/40 px-1.5 py-0.5 rounded-sm border border-white/10">
                          default
                        </span>
                      )}
                      <button
                        onClick={() => setEditing({ id: it.id, label: it.label, color: it.color })}
                        data-testid="reception-edit-btn"
                        className="p-1.5 rounded-sm text-white/40 hover:text-[#0055FF] hover:bg-[#0055FF]/10 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <PencilSimple size={14} />
                      </button>
                      <button
                        onClick={() => remove(it)}
                        data-testid="reception-delete-btn"
                        className="p-1.5 rounded-sm text-white/40 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash size={14} />
                      </button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function ColorPicker({ value, onChange }) {
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {PRESET_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          className={`w-6 h-6 rounded-sm border transition-transform ${value === c ? "scale-110 border-white" : "border-white/20 hover:border-white/40"}`}
          style={{ backgroundColor: c }}
          title={c}
          aria-label={c}
        />
      ))}
    </div>
  );
}

function RejectionCausesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/catalogs/rejection-causes");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!label.trim()) { toast.error("Ingresa una causa"); return; }
    setBusy(true);
    try {
      await api.post("/catalogs/rejection-causes", { label: label.trim() });
      toast.success("Causa agregada");
      setLabel("");
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar la causa "${it.label}"?`)) return;
    try {
      await api.delete(`/catalogs/rejection-causes/${it.id}`);
      toast.success("Eliminada");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="bg-[#111111] border border-white/10 rounded-sm">
      <div className="p-5 border-b border-white/10 flex items-center gap-3">
        <Tag size={20} className="text-[#FF3B30]" weight="duotone" />
        <div className="flex-1">
          <div className="text-[10px] font-mono-data uppercase tracking-widest text-white/50">
            Catálogo · Rechazos
          </div>
          <div className="font-display text-lg">Causas de Rechazo</div>
          <div className="text-xs text-white/50 mt-1">
            Aparecen como checkboxes al crear un nuevo rechazo por producto maltratado.
          </div>
        </div>
        <span
          data-testid="rejection-causes-count"
          className="font-mono-data text-xs px-2 py-0.5 rounded-sm bg-white/5 border border-white/10 text-white/60"
        >
          {items.length}
        </span>
      </div>
      <form onSubmit={add} className="p-4 border-b border-white/10 flex gap-2">
        <input
          data-testid="rejection-cause-input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Nueva causa (ej. Empaque dañado)"
          className="flex-1 px-3 py-2 rounded-sm bg-[#0A0A0A] border border-white/10 text-sm text-white placeholder:text-white/30 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] outline-none"
        />
        <button
          data-testid="rejection-cause-add"
          disabled={busy}
          className="flex items-center gap-1.5 px-4 py-2 rounded-sm bg-[#FF3B30] text-white text-xs font-semibold hover:brightness-110 active:scale-[0.98] transition-transform disabled:opacity-50"
        >
          <Plus size={14} /> Agregar
        </button>
      </form>
      <div className="max-h-[420px] overflow-y-auto">
        {loading ? (
          <div className="p-6 text-white/50 text-sm">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center border border-dashed border-white/10 m-4 rounded-sm">
            <div className="text-white/60 text-sm">Sin causas</div>
          </div>
        ) : (
          <ul>
            {items.map((it) => (
              <li
                key={it.id}
                data-testid="rejection-cause-row"
                className="flex items-center gap-3 px-4 py-2.5 border-b border-white/5 hover:bg-white/5 group"
              >
                <Warning size={13} className="text-[#FF3B30]/60 shrink-0" />
                <div className="flex-1 text-sm text-white">{it.label}</div>
                <button
                  onClick={() => remove(it)}
                  data-testid="rejection-cause-delete"
                  className="p-1.5 rounded-sm text-white/40 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
